
import React, { useState } from 'react';
import { SimulationConfig, AudioPreset } from '@/types';
import { PRESET_LIBRARY } from '@/data/presets';
import { AUDIO } from '@/services/AudioService';
import { CloudService } from '@/services/CloudService';

interface PresetLibraryProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
    cloudSaves?: any[];
}

export const PresetLibrary: React.FC<PresetLibraryProps> = ({ config, setConfig, cloudSaves = [] }) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [selectedInstrument, setSelectedInstrument] = useState<string>('All');
    const [selectedEchoType, setSelectedEchoType] = useState<string>('All');
    const [selectedClarity, setSelectedClarity] = useState<string>('All');
    const [presetSearch, setPresetSearch] = useState<string>('');

    const categories = ['All', ...Array.from(new Set(PRESET_LIBRARY.map(p => p.category)))];
    const instruments_filter = ['All', ...Array.from(new Set(PRESET_LIBRARY.filter(p => p.instrument).map(p => p.instrument!)))];
    const echoTypes = ['All', ...Array.from(new Set(PRESET_LIBRARY.filter(p => p.echoType).map(p => p.echoType!)))];
    const clarities = ['All', ...Array.from(new Set(PRESET_LIBRARY.filter(p => p.clarity).map(p => p.clarity!)))];
    
    const pinned = config.pinnedPresets || [];
    
    const filteredPresets = PRESET_LIBRARY.filter(p => {
        const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
        const matchesInstrument = selectedInstrument === 'All' || p.instrument === selectedInstrument;
        const matchesEcho = selectedEchoType === 'All' || p.echoType === selectedEchoType;
        const matchesClarity = selectedClarity === 'All' || p.clarity === selectedClarity;
        const matchesSearch = presetSearch === '' || 
                             p.name.toLowerCase().includes(presetSearch.toLowerCase()) ||
                             p.category.toLowerCase().includes(presetSearch.toLowerCase()) ||
                             (p.tags && p.tags.some(t => t.toLowerCase().includes(presetSearch.toLowerCase())));
        
        return matchesCategory && matchesInstrument && matchesEcho && matchesClarity && matchesSearch;
    });

    const sortedPresets = [...filteredPresets].sort((a, b) => {
        const aPinned = pinned.includes(a.name);
        const bPinned = pinned.includes(b.name);
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;
        return a.name.localeCompare(b.name);
    });

    const loadPreset = (preset: AudioPreset) => {
        AUDIO.resetEcho();
        setConfig(p => ({
            ...p,
            ...preset.settings,
            masterVolume: preset.settings.masterVolume ?? p.masterVolume ?? 1.0,
            melodyVolume: preset.settings.melodyVolume ?? p.melodyVolume ?? 0.6,
            fxVolume: preset.settings.fxVolume ?? p.fxVolume ?? 0.8,
            audioCompThreshold: preset.settings.audioCompThreshold ?? -12,
            audioCompRatio: preset.settings.audioCompRatio ?? 4
        }));
    };

    const togglePin = async (e: React.MouseEvent, presetName: string) => {
        e.stopPropagation();
        setConfig(p => {
            const current = p.pinnedPresets || [];
            const isPinned = current.includes(presetName);
            const nextPinned = isPinned
                ? current.filter(n => n !== presetName)
                : Array.from(new Set([presetName, ...current]));
            
            const defaultSave = (cloudSaves || []).find(s => s.isDefault);
            if (defaultSave) {
                CloudService.updateSave(defaultSave.id, { 
                    config: { ...p, pinnedPresets: nextPinned } 
                });
            }

            return { ...p, pinnedPresets: nextPinned };
        });
    };

    return (
        <div className="flex flex-col gap-4">
             <div className={`flex items-center px-4 py-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5 shadow-inner' : 'bg-white border-neutral-100 shadow-sm'}`}>
                <i className="fa-solid fa-search opacity-40 text-[10px] mr-3"></i>
                <input 
                    type="text" 
                    placeholder="Search presets or tags..." 
                    value={presetSearch}
                    onChange={(e) => setPresetSearch(e.target.value)}
                    className="bg-transparent outline-none w-full text-[10px] font-bold placeholder-opacity-40"
                />
            </div>

            <div className="flex flex-col gap-3">
                <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-2 mask-linear-r">
                    {categories.map(cat => (
                        <button 
                            key={cat} onClick={() => setSelectedCategory(cat)}
                            className={`text-[8px] font-black uppercase px-4 py-1.5 rounded-full border transition-all whitespace-nowrap shadow-sm ${selectedCategory === cat ? 'bg-emerald-600 border-emerald-500 text-white shadow-emerald-500/20' : (config.darkMode ? 'bg-black/20 border-white/5 text-white/40 hover:text-white/80' : 'bg-white border-neutral-100 text-neutral-400')}`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
                
                <div className="flex gap-1 overflow-x-auto custom-scrollbar pb-1">
                    <span className="text-[10px] font-bold uppercase opacity-60 self-center mr-2">Instrument</span>
                    {instruments_filter.map(inst => (
                        <button 
                            key={inst} onClick={() => setSelectedInstrument(inst)}
                            className={`text-[7px] font-black uppercase px-2.5 py-1 rounded-lg border transition-all whitespace-nowrap ${selectedInstrument === inst ? 'bg-blue-600 border-blue-500 text-white' : (config.darkMode ? 'bg-white/5 border-white/5 text-white/30 hover:text-white/60' : 'bg-white border-neutral-100 text-neutral-400 shadow-sm')}`}
                        >
                            {inst.replace(/_/g, ' ')}
                        </button>
                    ))}
                </div>

                <div className="flex gap-1 overflow-x-auto custom-scrollbar pb-1">
                    <span className="text-[10px] font-bold uppercase opacity-60 self-center mr-2">Echo Logic</span>
                    {echoTypes.map(type => (
                        <button 
                            key={type} onClick={() => setSelectedEchoType(type)}
                            className={`text-[7px] font-black uppercase px-2.5 py-1 rounded-lg border transition-all whitespace-nowrap ${selectedEchoType === type ? 'bg-purple-600 border-purple-500 text-white' : (config.darkMode ? 'bg-white/5 border-white/5 text-white/30 hover:text-white/60' : 'bg-white border-neutral-100 text-neutral-400 shadow-sm')}`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            <div className={`grid grid-cols-2 gap-2 mb-2 max-h-96 overflow-y-auto custom-scrollbar p-2 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-neutral-100 border-neutral-200'}`}>
                {sortedPresets.map(preset => {
                    const isPinned = pinned.includes(preset.name);
                    return (
                        <div key={preset.name} className="relative group">
                            <button 
                                onClick={() => loadPreset(preset)}
                                className={`w-full text-[10px] font-bold py-3.5 pl-3.5 pr-8 rounded-2xl text-left transition-all hover:translate-y-[-2px] hover:shadow-lg active:scale-95 leading-tight ${config.darkMode ? 'bg-black/40 border-white/5 text-white hover:bg-emerald-500/20' : 'bg-white border-neutral-100 text-neutral-800 hover:bg-emerald-50 shadow-sm'} ${isPinned ? 'border-emerald-500/30' : 'border'}`}
                            >
                                <div className="flex flex-col gap-0.5">
                                    <div className="flex justify-between items-center opacity-40 mb-1">
                                        <span className="text-[7px] uppercase tracking-wider">{preset.category}</span>
                                        {preset.instrument && <span className="text-[7px] italic">{preset.instrument.replace(/_/g, ' ')}</span>}
                                    </div>
                                    <span className="whitespace-normal break-words leading-snug">{preset.name}</span>
                                </div>
                            </button>
                            <button 
                                onClick={(e) => togglePin(e, preset.name)}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl transition-all ${isPinned ? 'text-emerald-500 opacity-100' : 'text-neutral-500/20 opacity-0 group-hover:opacity-100 hover:text-emerald-500'}`}
                            >
                                <i className={`fa-solid fa-thumbtack text-[9px] ${isPinned ? '' : 'rotate-45'}`}></i>
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
